'use client';

import { c as _c } from "react/compiler-runtime";
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import dynamic from 'next/dynamic';
import type { V1ToV2MigrationEntrySource } from '@/lib/analytics/client/events';
import type { VaultV1Migration } from '@/lib/procedures/vaultV1Migration.contract';
import { useChainNavigation } from '@/hooks/useChainNavigation';
import { trackV1ToV2MigrationEntryClicked } from '@/lib/analytics/client/trackers/migration';

// Dynamic import: the modal carries `@morpho-org/morpho-sdk`,
// `@morpho-org/blue-sdk-viem`, and a heavy GraphQL hook. We only need it after
// the user clicks Upgrade — keep it out of the dashboard row chunk where
// `useV1ToV2MigrationTarget` runs once per row.
const V1ToV2MigrationModal = dynamic(() => import('../V1ToV2MigrationModal').then(m => m.V1ToV2MigrationModal), {
  ssr: false
});
const MIGRATION_DIALOG_OPTIONS = {
  maxWidth: '680px',
  className: 'bg-card border-[0.5px] border-border'
} as const;

/**
 * Returns a callback that opens the V1 → V2 migration modal for `target`.
 *
 * `source` labels which entry point fired the open so the analytics click event
 * can be segmented (e.g. dashboard button vs. vault-page banner).
 *
 * Syncs the app-level `targetChain` to the vault's chain before opening:
 * the modal mounts `useV1MigrateToV2Transaction`, whose SDK calls go through
 * `Web3Context.viemClient.morpho`, which is bound to `targetChain.id`. Without
 * this sync, opening the modal from a row whose vault is on a different
 * chain than `targetChain` would leave the SDK calls failing with
 * `ChainIdMismatchError`, with no recovery affordance.
 *
 * If the chain sync throws (e.g. `InterfaceChainUtils.getMetadata` for an
 * unknown chainId), the modal still opens — its "Switch chain" branch handles
 * the wrong-chain state and gives the user an actionable affordance.
 */
export const useOpenMigrationModal = (target, source) => {
  const $ = _c(9);
  const {
    toggleDialog
  } = useDialogContext();
  let t0;
  if ($[0] !== target.chainId) {
    t0 = {
      chainId: target.chainId
    };
    $[0] = target.chainId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    syncChainSwitch
  } = useChainNavigation(t0);
  let t1;
  if ($[2] !== source || $[3] !== syncChainSwitch || $[4] !== target.chainId || $[5] !== target.v1VaultAddress || $[6] !== target.v2VaultAddress || $[7] !== toggleDialog) {
    t1 = () => {
      trackV1ToV2MigrationEntryClicked({
        source,
        chainId: target.chainId,
        v1VaultAddress: target.v1VaultAddress,
        v2VaultAddress: target.v2VaultAddress
      });
      try {
        syncChainSwitch();
      } catch {}
      toggleDialog(<V1ToV2MigrationModal chainId={target.chainId} v1VaultAddress={target.v1VaultAddress} v2VaultAddress={target.v2VaultAddress} />, MIGRATION_DIALOG_OPTIONS);
    };
    $[2] = source;
    $[3] = syncChainSwitch;
    $[4] = target.chainId;
    $[5] = target.v1VaultAddress;
    $[6] = target.v2VaultAddress;
    $[7] = toggleDialog;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};